// "Connect Symone to Claude" (simple-mode D32).
//
// 1. Pick a client.  2. Create a connection: POST /workspaces/{id}/api-keys with the
// client's label as the key name (so it shows on the developer API Keys page under that
// name).  3. Show the client's setup snippet with the secret inlined — the only time the
// secret is ever on screen. "Done" drops it from memory; leaving the page does too.
//
// Primary copy avoids "API key", "Bearer" and "workspace id"; those appear only inside
// the copy-paste snippets. The MCP URL comes from mcpEndpoint() (canonical public host,
// https://mcp.symone.com unless VITE_GATEWAY_PUBLIC_URL overrides it at build).
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch, isApiError } from '../api/client'
import { mcpEndpoint } from '../api/config'
import { qk } from '../api/queries'
import type { ApiKeyWithSecret } from '../api/types'
import { useSession } from '../context/SessionContext'
import { useMode } from '../context/ModeContext'
import { useCan } from '../rbac'
import { CodeBlock } from '../components/CodeBlock'
import { Icon } from '../components/Icon'
import { Loading, PageHeader } from '../components/ui'
import { CLIENTS, clientSnippet, type ClaudeClient } from './connectClaude'

export function ConnectClaudeView() {
  const { workspaceId } = useSession()
  const mode = useMode()
  const canCreate = useCan('create_api_key')
  const qc = useQueryClient()
  const [client, setClient] = useState<ClaudeClient>('claude-code')
  const [doneAs, setDoneAs] = useState<string | null>(null)
  const chosen = CLIENTS.find((c) => c.id === client) ?? CLIENTS[0]

  const create = useMutation({
    mutationFn: (name: string) =>
      apiFetch<ApiKeyWithSecret>(`/workspaces/${workspaceId}/api-keys`, {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.apiKeys(workspaceId) }),
  })

  const header = (
    <PageHeader
      title="Connect to Claude"
      description="Let Claude use your connected apps through Symone. Three steps, about a minute."
    />
  )

  if (mode.loading) return <div className="page page--narrow">{header}<Loading /></div>
  if (!mode.connectClaude) {
    return (
      <div className="page page--narrow">
        {header}
        <p className="muted" role="note">Connect to Claude is not included in this workspace’s plan.</p>
      </div>
    )
  }

  const url = mcpEndpoint(workspaceId ?? '')
  const created = create.data
  const snippet = created ? clientSnippet(client, url, created.key) : null

  return (
    <div className="page page--narrow">
      {header}

      <p className="muted connect-note">
        Works with Claude Code, Cursor, Claude Desktop (through its config file) and other MCP
        clients that accept a header. The “Connectors” screens in claude.ai and Claude Desktop
        need sign-in support that Symone doesn’t offer yet.
      </p>

      <ol className="stepper">
        <li className="step step--current">
          <div className="step-body">
            <h2 className="step-title">1. Which client?</h2>
            <div className="client-picker" role="radiogroup" aria-label="Client">
              {CLIENTS.map((c) => (
                <label key={c.id} className={'client-option' + (client === c.id ? ' active' : '')}>
                  <input
                    type="radio"
                    name="claude-client"
                    value={c.id}
                    checked={client === c.id}
                    disabled={!!created}
                    onChange={() => { setClient(c.id); setDoneAs(null) }}
                  />
                  <span className="cell-strong">{c.label}</span>
                  <span className="cell-sub">{c.hint}</span>
                </label>
              ))}
            </div>
          </div>
        </li>

        <li className="step step--current">
          <div className="step-body">
            <h2 className="step-title">2. Create a connection</h2>
            {!canCreate ? (
              <p className="muted" role="note">
                Only a workspace owner or admin can create a connection. Ask one of them to set
                this up, or to make you an admin.
              </p>
            ) : created ? (
              <p className="muted">Created “{created.name}”.</p>
            ) : (
              <>
                <p>Symone creates a private connection for {chosen.label} in this workspace.</p>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={create.isPending}
                  onClick={() => { setDoneAs(null); create.mutate(chosen.label) }}
                >
                  {create.isPending ? 'Creating…' : `Create connection for ${chosen.label}`}
                </button>
                {create.error && (
                  <p className="form-error" role="alert">
                    {isApiError(create.error) && create.error.status === 403
                      ? 'Your role can’t create connections in this workspace.'
                      : 'Couldn’t create the connection. Try again.'}
                  </p>
                )}
              </>
            )}
            {doneAs && !created && (
              <p className="muted" role="status">
                <Icon name="check" size={14} /> {doneAs} is set up. Its connection is saved under that
                name; create another one any time.
              </p>
            )}
          </div>
        </li>

        <li className="step step--current">
          <div className="step-body">
            <h2 className="step-title">3. Paste this into {chosen.label}</h2>
            {snippet && created ? (
              <>
                <p className="warn-note">
                  Copy this now. It contains a secret that is shown only once and can’t be shown again.
                </p>
                <CodeBlock code={snippet.code} label={snippet.label} />
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => { setDoneAs(created.name); create.reset() }}
                >
                  Done
                </button>
              </>
            ) : (
              <p className="muted">Your setup appears here after step 2. Server address: <code>{url}</code></p>
            )}
          </div>
        </li>
      </ol>
    </div>
  )
}
